import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  DocumentoItemDto,
  DocumentosDescargaRequestDto,
  DocumentosDescargaResponseDto,
  ListaDocumentosResponseDto,
  SolicitudDocumentosRequestDto,
  SolicitudDocumentosResponseDto,
} from '../dto/expediente/documentos.dto';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { DescargaArchivosMensajes } from '../constants/mensajes-descarga-archivos';
import { UtilService } from './util.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentosService {
  private apiUrl = environment.apiUrl;
  private baseUrl = '/tramites';
  private baseSSM = '/ssm';

  public headers = new HttpHeaders();

  constructor(
    private http: HttpClient,
    private toastService: ToastrService,
    private utilService: UtilService
  ) {}

  getListaDocumentosByExpediente(
    expedienteId: number,
    esInsolvencia = false,
    numeroRadicado?: string,
    numeroProceso?:string,
  ): Observable<ListaDocumentosResponseDto> {
    return this.http.get<ListaDocumentosResponseDto>(
      `${this.apiUrl}${this.baseUrl}/documentos/${expedienteId}/${esInsolvencia}/${numeroRadicado}:/${numeroProceso}`
    );
  }

  getDocumentosDescarga(
    downloadRequest: DocumentosDescargaRequestDto,
    idExpediente: number,
    entidad: string
  ): void {
    this.headers = this.headers.append('skip', 'true');

    this.toastService.info(
      DescargaArchivosMensajes.DESCARGA_INICIADA,
      DescargaArchivosMensajes.DESCARGA_INICIADA_TITULO
    );

    this.http
      .post<DocumentosDescargaResponseDto>(
        `${this.apiUrl}${this.baseUrl}/documentos`,
        downloadRequest,
        { headers: this.headers }
      )
      .subscribe(
        (response: DocumentosDescargaResponseDto) => {
            if (response.url) {
              window.open(response.url, '_blank');
              this.utilService.setStatus(DescargaArchivosMensajes.ESTADO_FINALIZADO);
            }else if (response.documentos && response.documentos.length > 0) {
               response.documentos.forEach(doc => {
                 if (doc.url) {
                   window.open(doc.url, '_blank');
                 }
               });
               this.utilService.setStatus(DescargaArchivosMensajes.ESTADO_FINALIZADO);
             }
            else if (response.documento) {
              this.showSuccessMessage(idExpediente, entidad);
              this._downloadBase64File(response.documento, response.nombre);
            } else if (response.zipBase64) {
              this.showSuccessMessage(idExpediente, entidad);
              const fileName = response.nombre || 'documentos.zip';
              this._downloadBase64File(response.zipBase64, fileName);
            } else {
              this.utilService.setStatus(DescargaArchivosMensajes.ESTADO_FALLIDO);
              this.toastService.error(
                DescargaArchivosMensajes.DESCARGA_FALLIDA,
                DescargaArchivosMensajes.DESCARGA_FALLIDA_TITULO
              );
            }
        },
        error => {
          this.utilService.setStatus(DescargaArchivosMensajes.ESTADO_FALLIDO);
          this.toastService.error(
            DescargaArchivosMensajes.DESCARGA_FALLIDA,
            DescargaArchivosMensajes.DESCARGA_FALLIDA_TITULO
          );
        }
      );
  }

  showSuccessMessage(idExpediente: number, entidad: string) {
    this.utilService.setStatus(DescargaArchivosMensajes.ESTADO_FINALIZADO);
    this.toastService.success(
      DescargaArchivosMensajes.DESCARGA_SATISFACTORIA.replace(
        '[Número de expediente]',
        idExpediente.toString()
      ).replace('[Entidad]', entidad),
      DescargaArchivosMensajes.DESCARGA_SATISFACTORIA_TITULO
    );
  }

  showErrorMessageGeneral(titulo: string, mensaje: string) {
    this.toastService.error(
      mensaje,
      titulo
    );
  }

  showWarningMessageGeneral(titulo: string, mensaje: string) {
    this.toastService.warning(
      mensaje,
      titulo
    );
  }

  _downloadBase64File(base64: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = `data:application/octet-stream;base64,${base64}`;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  crearSolicitudDocumentos(
    emailRequest: SolicitudDocumentosRequestDto
  ): Observable<SolicitudDocumentosResponseDto> {
    return this.http.post<SolicitudDocumentosResponseDto>(
      `${this.apiUrl}${this.baseUrl}/documentos/solicitud-documentos`,
      emailRequest
    );
  }

  getDocumentosByIdProceso(idProceso: number): Observable<DocumentoItemDto[]> {
    return this.http.get<DocumentoItemDto[]>(
      `${this.apiUrl}${this.baseSSM}/documentos/${idProceso}`
    );
  }

  getDocumentosDescargaSsm(
    idExpediente: number,
    entidad: string,
    idDocumento: number
  ): void {
    this.headers = this.headers.append('skip', 'true');

    this.toastService.info(
      DescargaArchivosMensajes.DESCARGA_INICIADA,
      DescargaArchivosMensajes.DESCARGA_INICIADA_TITULO
    );

    this.http
      .get<any>(`${this.apiUrl}${this.baseSSM}/documento/${idDocumento}`)
      .pipe(
        finalize(() => {
          this.toastService.success(
            DescargaArchivosMensajes.DESCARGA_SATISFACTORIA.replace(
              '[Número de expediente]',
              idExpediente.toString()
            ).replace('[Entidad]', entidad),
            DescargaArchivosMensajes.DESCARGA_SATISFACTORIA_TITULO
          );
        })
      )
      .subscribe(
        (response: any) => {
          if (response) {
            this._downloadBase64File(response.zipBase64, 'documentos.zip');
            // } else if (response.zipBase64) {
            //   this._downloadBase64File(response, 'documentos.zip');
          } else {
            this.utilService.setStatus(DescargaArchivosMensajes.ESTADO_FALLIDO);
            this.toastService.error(
              DescargaArchivosMensajes.DESCARGA_FALLIDA,
              DescargaArchivosMensajes.DESCARGA_FALLIDA_TITULO
            );
          }
        },
        error => {
          this.utilService.setStatus(DescargaArchivosMensajes.ESTADO_FALLIDO);
          this.toastService.error(
            DescargaArchivosMensajes.DESCARGA_FALLIDA,
            DescargaArchivosMensajes.DESCARGA_FALLIDA_TITULO
          );
        }
      );
  }

  obtenerDocumentosPorTramite(tramiteId: string): Observable<Array<{ nombre: string; url: string }>> {
    return this.http.get<Array<{ nombre: string; url: string }>>(
      `${this.apiUrl}/tramites/${tramiteId}/documentos`
    );
  }

}
